// Session BCE: "Send app details" on an engineer's row (the Engineers page and the Engineers section of a
// subcontractor's page). It replaces the password link and the invite: engineers have no password, they sign in with
// their email and an emailed code. Two steps, branded, never a native confirm (the BRANDED CONFIRM rule): the first
// press asks the route's own preview, which names the engineer and the address the draft will go to; the second press
// leaves the Gmail DRAFT. Nothing is sent: the office opens Gmail and presses Send. A self-contained per-row
// component, so one row's state cannot bleed into another.
import { useState } from "react";
import { ApiError, api } from "../lib/api";
import type { EngineerAppDetailsPreview } from "../lib/api";

const btnCls =
  "rounded-ele border border-ele-purple-accent px-2.5 py-1 text-xs text-ele-body transition hover:border-ele-green hover:text-ele-green disabled:opacity-50";

function refusal(err: unknown): string {
  if (err instanceof ApiError) {
    const message = typeof err.body?.message === "string" ? err.body.message : null;
    if (message) return message;
    if (err.code === "not_found") return "That engineer could not be found.";
  }
  return "Could not prepare the email. Please try again.";
}

export function SendAppDetailsButton({ userId, name }: { userId: string; name: string }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<EngineerAppDetailsPreview | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function ask() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      setPreview(await api.engineers.previewAppDetails(userId));
    } catch (err) {
      setError(refusal(err));
    } finally {
      setBusy(false);
    }
  }

  async function draft() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.engineers.sendAppDetails(userId);
      setPreview(null);
      setNotice(`Draft ready in Gmail to ${res.to}. Open Gmail and press Send.`);
    } catch (err) {
      setError(refusal(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="inline-flex flex-col gap-1">
      {preview ? (
        <span role="group" aria-label={`Send ${name} the app details`} className="inline-flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-ele-strong">Draft the app details for {preview.engineer} to {preview.to}?</span>
          <button type="button" className={btnCls} disabled={busy} onClick={() => void draft()}>
            {busy ? "Drafting..." : "Yes, draft it"}
          </button>
          <button type="button" className={btnCls} disabled={busy} onClick={() => setPreview(null)}>
            Cancel
          </button>
        </span>
      ) : (
        <button type="button" className={btnCls} disabled={busy} onClick={() => void ask()}>
          {busy ? "Checking..." : "Send app details"}
        </button>
      )}
      {notice && (
        <span role="status" className="text-xs text-ele-strong">
          {notice}
        </span>
      )}
      {error && (
        <span role="alert" className="text-xs text-ele-danger">
          {error}
        </span>
      )}
    </span>
  );
}
